/**
 * Hook to preview multi-country interval scheduling.
 * Computes per-drop executeAfter times for either interval mode.
 */

import { useMemo } from 'react';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { computeMultiCountryIntervals } from '../shared';
import type { IntervalMode, PerItemScheduleEntry } from '../shared';

export interface UseMultiCountryIntervalsParams {
  ids: string[];
  mergedDrops: PreparedDrop[];
  baseUtcIso: string;
  intervalMinutes: number;
  mode: IntervalMode;
  enabled: boolean;
}

export interface IntervalPreviewItem {
  id: string;
  countryKey: string;
  executeAfter: string;
}

export function useMultiCountryIntervals(params: UseMultiCountryIntervalsParams) {
  const { ids, mergedDrops, baseUtcIso, intervalMinutes, mode, enabled } = params;

  const entries = useMemo((): PerItemScheduleEntry[] => {
    if (!enabled || !baseUtcIso || intervalMinutes <= 0) return [];
    return computeMultiCountryIntervals({
      ids,
      mergedDrops,
      baseUtcIso,
      intervalMinutes,
      mode,
    });
  }, [ids, mergedDrops, baseUtcIso, intervalMinutes, mode, enabled]);

  const preview = useMemo((): IntervalPreviewItem[] => {
    return entries.map((e) => ({
      id: e.id,
      countryKey: e.countryKey,
      executeAfter: e.payload.executeAfter ?? '',
    }));
  }, [entries]);

  const countByCountry = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of entries) {
      map.set(e.countryKey, (map.get(e.countryKey) ?? 0) + 1);
    }
    return map;
  }, [entries]);

  return { entries, preview, countByCountry };
}
